/**
 * Install-level facts the client preloads before login.
 *
 * The endpoint is public, so it only ever carries values that are safe for
 * anyone to read.
 *
 * @category data
 */

import { useQuery } from "@tanstack/react-query";

import { api, ApiResponse } from "_/utils";

const SCOPE = "server";
export const KEYS = {
  preload: () => [SCOPE, "preload"],
};

export type ServerPreload = {
  /**
   * Whether this install can send email.
   *
   * `false` on airgapped installs configured with `SMTP_DISABLED=true`. Email-dependent
   * actions (forgot password, resend verification) are hidden, and invitation and password
   * reset links are shown to admins instead of being emailed.
   */
  mailEnabled: boolean;

  /**
   * The deployment environment, such as `prod` or `stage`, which labels the client's telemetry.
   */
  environment: string;

  /**
   * Where the client sends its logs and traces, or `null` when it sends none, as on Basis
   * Standalone.
   */
  telemetryEndpoint: string | null;
};

export function useServerPreload() {
  async function fetchPreload(): Promise<ServerPreload> {
    const response = await api.get<ApiResponse<ServerPreload>>("/preload");
    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.preload(),
    queryFn: fetchPreload,
    staleTime: Infinity,
  });
}

/**
 * Whether this install can send email, or `undefined` while still loading.
 */
export function useMailEnabled(): boolean | undefined {
  const { data } = useServerPreload();
  return data?.mailEnabled;
}
